import React, { useState, useEffect } from 'react';
import { AIProvider, Prediction, CrawlResponse } from '../types';
import { generatePrediction, getAIProviders, getPrompt, savePrompt, getMatchPredictions, publishToPsynet } from '../api';
import { toast } from 'react-toastify';
import { User } from '../api/auth';
import { dataDisplayStyles as styles } from './DataDisplay';
import { useAdminList } from '../hooks/useAdminList';

interface PredictionPanelProps {
  currentMatch: any;
  data: CrawlResponse;
  setData: React.Dispatch<React.SetStateAction<CrawlResponse>>;
  isLoading?: boolean;
  currentUser?: User | null;
  setActiveTab: (tab: 'matches' | 'raw' | 'predictions') => void;
}

const PredictionPanel: React.FC<PredictionPanelProps> = ({
  currentMatch,
  data,
  setData,
  isLoading = false,
  currentUser,
  setActiveTab,
}) => {
  const [selectedModel, setSelectedModel] = useState<string>('gemini');
  const [aiProviders, setAiProviders] = useState<AIProvider[]>([]);
  const [generating, setGenerating] = useState(false);
  const [customPrompt] = useState('');
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [editablePrompt, setEditablePrompt] = useState('');
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [allPredictions, setAllPredictions] = useState<Prediction[]>([]);
  const [selectedPredictionIndex, setSelectedPredictionIndex] = useState(0);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [publishingToPsynet, setPublishingToPsynet] = useState(false);
  const [showRepublishConfirm, setShowRepublishConfirm] = useState(false);

  const isAdministrator = currentUser?.role === 'administrator';

  // 관리자 목록 (administrator만 자동 조회)
  const { adminList } = useAdminList({ immediate: !!isAdministrator });
  const [filterAdminId, setFilterAdminId] = useState<number | undefined>(undefined);

  useEffect(() => {
    loadAIProviders();
  }, []);

  const loadAIProviders = async () => {
    try {
      const providers = await getAIProviders();
      setAiProviders(providers);
      if (providers.length > 0) {
        setSelectedModel(providers[0].name);
      }
    } catch (error) {
      console.error('Failed to load AI providers:', error);
    }
  };

  // Debug log to understand data structure
  useEffect(() => {
    if (currentMatch && currentMatch?.match?.sport === 'baseball') {
      console.log('Current baseball match structure:', currentMatch);
      console.log('Starting pitchers in match:', currentMatch.match?.starting_pitchers);
    }
  }, [currentMatch]);

  // Load predictions from crawl response or fetch separately
  // 경기 변경 시 예측 데이터 초기화 및 로드
  useEffect(() => {
    // 경기 변경 시 먼저 예측 데이터 초기화
    setAllPredictions([]);
    setSelectedPredictionIndex(0);

    if (!currentMatch) {
      console.log('No currentMatch, clearing predictions');
      return;
    }

    // Check if predictions are already in the crawl response
    if (currentMatch?.predictions && Array.isArray(currentMatch.predictions)) {
      const matchId = currentMatch.match_id || currentMatch.match?.match_id || 0;

      const formattedPredictions = currentMatch.predictions.map((pred: any) => ({
        prediction_id: pred.prediction_id || Math.random(),
        match_id: pred.match_id || matchId,
        ai_model: pred.model || pred.ai_model,
        report: pred.report,
        version: pred.version || 1,
        created_at: pred.created_at || new Date().toISOString(),
        prompt_template: pred.prompt_template || null,
        prompt_version: pred.prompt_version || null
      }));

      const sortedPredictions = formattedPredictions.sort((a: any, b: any) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setAllPredictions(sortedPredictions);
      console.log('Loaded predictions from crawl response for match:', currentMatch.match?.home_team, 'vs', currentMatch.match?.away_team, sortedPredictions);
    } else {
      console.log('No predictions found in crawl response for match:', currentMatch.match?.home_team, 'vs', currentMatch.match?.away_team);
    }
  }, [currentMatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // filterAdminId 변경 시 예측 데이터 재로드 (administrator만)
  useEffect(() => {
    if (!isAdministrator || !currentMatch) return;

    const matchId = currentMatch.match_id || currentMatch.match?.match_id || (currentMatch as any).game_id || currentMatch.match?.game_id;
    if (matchId) {
      loadAllPredictions(matchId, filterAdminId);
    }
  }, [filterAdminId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAllPredictions = async (matchId: number | string, adminIdFilter?: number) => {
    if (!currentMatch) return;

    setLoadingPredictions(true);
    try {
      const sport = currentMatch.match?.sport || 'soccer';
      const result = await getMatchPredictions(sport, matchId, adminIdFilter);

      if (result && result.predictions) {
        const sortedPredictions = result.predictions.sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setAllPredictions(sortedPredictions);
        if (selectedPredictionIndex >= sortedPredictions.length) {
          setSelectedPredictionIndex(0);
        }
      }
    } catch (error) {
      console.error('Failed to load predictions:', error);
    } finally {
      setLoadingPredictions(false);
    }
  };

  const handlePublishToPsynet = async () => {
    if (!currentMatch) return;

    const matchId = currentMatch.match_id || currentMatch.match?.match_id;

    if (!matchId) {
      toast.error('경기 ID가 없음. 먼저 경기를 저장해.');
      return;
    }

    setPublishingToPsynet(true);

    try {
      console.log('Publishing to Psynet with matchId:', matchId, 'type:', typeof matchId);

      const result = await publishToPsynet(matchId, false);

      if (result.status === 'already_published') {
        setPublishingToPsynet(false);
        setShowRepublishConfirm(true);
        return;
      }

      toast.success(`Psynet 발행 성공: ${result.home_team} vs ${result.away_team} (${result.ai_model})`);
      console.log('Psynet publish result:', result);
    } catch (error: any) {
      console.error('Failed to publish to Psynet:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Psynet 발행 실패';
      toast.error(errorMsg);
    } finally {
      setPublishingToPsynet(false);
    }
  };

  const performPublish = async () => {
    if (!currentMatch) return;

    const matchId = currentMatch.match_id || currentMatch.match?.match_id;

    if (!matchId) {
      toast.error('경기 ID가 없음. 먼저 경기를 저장해.');
      return;
    }

    setPublishingToPsynet(true);
    setShowRepublishConfirm(false);

    try {
      console.log('Force publishing to Psynet with matchId:', matchId, 'type:', typeof matchId);

      const result = await publishToPsynet(matchId, true);

      toast.success(`Psynet 재발행 성공: ${result.home_team} vs ${result.away_team} (${result.ai_model})`);
      console.log('Psynet publish result:', result);
    } catch (error: any) {
      console.error('Failed to publish to Psynet:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Psynet 발행 실패';
      toast.error(errorMsg);
    } finally {
      setPublishingToPsynet(false);
    }
  };

  const handleGeneratePrediction = async () => {
    if (!currentMatch) return;

    setGenerating(true);
    try {
      const fullMatchData = currentMatch?.match || currentMatch;

      const cleanMatchData = { ...fullMatchData };
      if ('match_id' in cleanMatchData) {
        delete cleanMatchData.match_id;
      }

      if (cleanMatchData.head_to_head && Array.isArray(cleanMatchData.head_to_head)) {
        cleanMatchData.head_to_head = {
          records: cleanMatchData.head_to_head
        };
        console.log('Converted head_to_head from array to dict structure');
      }

      console.log('Sending match data to server:', cleanMatchData);
      if (cleanMatchData.sport === 'baseball') {
        console.log('Baseball starting_pitchers data:', cleanMatchData.starting_pitchers);
      }

      const result = await generatePrediction(
        selectedModel,
        cleanMatchData,
        customPrompt || undefined
      );

      if (result && result.prediction) {
        let matchId = result.match_id || currentMatch.match_id || currentMatch.match?.match_id || 0;

        if (result.match_id && result.match_id !== 0) {
          if (currentMatch) {
            currentMatch.match_id = result.match_id;
            if (currentMatch.match) {
              currentMatch.match.match_id = result.match_id;
            }
          }
          matchId = result.match_id;
        }

        const newPrediction: Prediction = {
          prediction_id: result.prediction_id || Math.floor(Math.random() * 1000000),
          match_id: matchId,
          ai_model: result.provider,
          report: result.prediction,
          version: allPredictions.filter(p => p.ai_model === result.provider).length + 1,
          created_at: new Date().toISOString(),
          prompt_template: result.prompt_template || null,
          prompt_version: null
        };

        const updatedPredictions = [newPrediction, ...allPredictions];
        setAllPredictions(updatedPredictions);
        setSelectedPredictionIndex(0);

        if (currentMatch) {
          if (!currentMatch.predictions) {
            currentMatch.predictions = [];
          }
          currentMatch.predictions = updatedPredictions.map(pred => ({
            prediction_id: pred.prediction_id,
            model: pred.ai_model,
            report: pred.report,
            version: pred.version,
            created_at: pred.created_at,
            prompt_template: pred.prompt_template
          }));
          setData({...data});
        }

        const reloadId = matchId || (currentMatch as any)?.game_id || currentMatch?.match?.game_id;
        if (reloadId) {
          loadAllPredictions(reloadId, filterAdminId).then(() => {
            console.log(`Predictions reloaded from server for match_id: ${reloadId}`);
          });
        }

        setActiveTab('predictions');

        if (result.db_saved && result.match_id) {
          const version = allPredictions.filter(p => p.ai_model === result.provider).length + 1;
          toast.success(`${selectedModel.toUpperCase()} 예측 리포트 생성 완료 (버전 ${version}) - DB 저장 완료 (Match ID: ${result.match_id})`);
        } else if (result.db_saved) {
          toast.warning(`${selectedModel.toUpperCase()} 예측 리포트는 생성되었지만 Match ID 생성 실패`);
        } else {
          toast.warning(`${selectedModel.toUpperCase()} 예측 리포트는 생성되었지만 DB 저장 실패`);
        }
      }
    } catch (error) {
      console.error('Failed to generate prediction:', error);
      toast.error('예측 리포트 생성에 실패했음.');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('클립보드에 복사되었음.');
  };

  const viewPrompt = async () => {
    if (!currentMatch) return;

    const sport = currentMatch.match?.sport || (currentMatch as any)?.sport || 'soccer';

    try {
      const promptData = await getPrompt(sport);
      const template = promptData.prompt?.template || '';
      setEditablePrompt(template);
      setShowPromptEditor(true);
    } catch (error) {
      console.error('Failed to load prompt:', error);
      toast.error('프롬프트를 불러올 수 없음.');
    }
  };

  const handleSavePrompt = async () => {
    if (!currentMatch) return;

    const sport = currentMatch.match?.sport || (currentMatch as any)?.sport || 'soccer';

    setSavingPrompt(true);
    try {
      const result = await savePrompt(sport, { prompt: editablePrompt });
      toast.success(result.message);
      setShowPromptEditor(false);
    } catch (error) {
      console.error('Failed to save prompt:', error);
      toast.error('프롬프트 저장에 실패했음.');
    } finally {
      setSavingPrompt(false);
    }
  };

  return (
    <>
      {/* Prompt Modal */}
      {showPromptModal && (
        <div style={styles.modalOverlay} onClick={() => setShowPromptModal(false)}>
          <div style={{...styles.modalContent, maxWidth: '800px', width: '90%'}} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3>사용된 프롬프트</h3>
              <button
                style={styles.modalCloseButton}
                onClick={() => setShowPromptModal(false)}
              >
                ✕
              </button>
            </div>
            <div style={styles.modalBody}>
              <pre style={styles.modalPre}>{modalContent}</pre>
            </div>
            <div style={styles.modalFooter}>
              <button
                style={styles.modalCopyButton}
                onClick={() => {
                  copyToClipboard(modalContent);
                }}
              >
                복사
              </button>
              <button
                style={styles.modalConfirmButton}
                onClick={() => setShowPromptModal(false)}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prompt Editor Modal */}
      {showPromptEditor && (
        <div style={styles.modalOverlay}>
          <div style={{...styles.modalContent, maxWidth: '800px', width: '90%'}} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>{currentMatch?.match?.sport} 종목 프롬프트 편집기</h3>
              <button
                style={styles.modalCloseButton}
                onClick={() => setShowPromptEditor(false)}
              >
                ✕
              </button>
            </div>
            <div style={styles.modalBody}>
              <textarea
                value={editablePrompt}
                onChange={(e) => setEditablePrompt(e.target.value)}
                style={styles.promptTextarea}
                placeholder={"프롬프트를 입력해야 함..."}
              />
              <div style={styles.promptHint}>
                <p style={{ margin: 0 }}>• {'{match_data}'}는 경기 데이터로 자동 치환됨.</p>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button
                style={styles.modalSecondaryButton}
                onClick={() => {
                  copyToClipboard(editablePrompt);
                }}
              >
                복사
              </button>
              <button
                style={styles.modalPrimaryButton}
                onClick={handleSavePrompt}
                disabled={savingPrompt}
              >
                {savingPrompt ? '저장 중...' : '저장'}
              </button>
              <button
                style={styles.modalCancelButton}
                onClick={() => setShowPromptEditor(false)}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Republish Confirmation Modal */}
      {showRepublishConfirm && (
        <div style={styles.modalOverlay} onClick={() => setShowRepublishConfirm(false)}>
          <div style={{...styles.modalContent, maxWidth: '500px', width: '90%'}} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3>발행 내역 확인</h3>
              <button
                style={styles.modalCloseButton}
                onClick={() => setShowRepublishConfirm(false)}
              >
                ✕
              </button>
            </div>
            <div style={styles.modalBody}>
              <p style={{fontSize: '16px', lineHeight: '1.6', margin: 0}}>
                기존 작성 내역이 있음.<br/>
                강제 실행하시겠습니까?
              </p>
              <p style={{fontSize: '14px', color: '#6c757d', marginTop: '10px', marginBottom: 0}}>
                (임시저장은 유지)
              </p>
            </div>
            <div style={styles.modalFooter}>
              <button
                style={{...styles.modalConfirmButton, backgroundColor: '#6c757d'}}
                onClick={() => setShowRepublishConfirm(false)}
              >
                취소
              </button>
              <button
                style={{...styles.modalConfirmButton, backgroundColor: '#dc3545'}}
                onClick={performPublish}
                disabled={publishingToPsynet}
              >
                {publishingToPsynet ? '발행 중...' : '강제 실행'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main prediction content */}
      {!currentMatch ? (
        <div style={styles.predictionContent}>
          <div style={{textAlign: 'center', padding: '40px', color: '#6c757d'}}>
            <div style={{fontSize: '18px', marginBottom: '10px'}}>
              경기 데이터가 없음.
            </div>
            <div style={{fontSize: '14px'}}>
              경기를 선택해.
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* 예측 생성 컨트롤 - AI 모델 선택 + 예측 생성 버튼 + 프롬프트 확인 (한 행) */}
          <div style={styles.predictionControlRow}>
            <div style={styles.modelSelectGroup}>
              <label style={styles.modelSelectLabel}>AI 모델:</label>
              <select
                style={styles.modelSelect}
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
              >
                {aiProviders.map((provider) => (
                  <option key={provider.name} value={provider.name}>
                    {provider.display_name}
                  </option>
                ))}
              </select>
            </div>
            <button
              style={styles.generateButton}
              onClick={handleGeneratePrediction}
              disabled={generating}
            >
              {generating ? '생성 중...' : '예측 생성'}
            </button>
            {/* 관리자 필터 - administrator만 표시 */}
            {isAdministrator && (
              <div style={styles.modelSelectGroup}>
                <label style={styles.modelSelectLabel}>담당자:</label>
                <select
                  style={styles.modelSelect}
                  value={filterAdminId ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFilterAdminId(val === '' ? undefined : Number(val));
                  }}
                >
                  <option value="">전체</option>
                  {adminList.map((admin) => (
                    <option key={admin.admin_id} value={admin.admin_id}>
                      {admin.display_name} ({admin.role})
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ flex: 1 }} />
            <button
              style={styles.promptButton}
              onClick={viewPrompt}
            >
              종목 프롬프트 확인
            </button>
          </div>

          {/* Display all predictions */}
          {loadingPredictions ? (
            <div style={styles.predictionContent}>
              <p style={{textAlign: 'center', color: '#6c757d', padding: '20px'}}>
                예측 리포트를 불러오는 중...
              </p>
            </div>
          ) : allPredictions.length > 0 ? (
            <>
              {/* Selected Prediction Display */}
              {allPredictions[selectedPredictionIndex] && (
                <div style={styles.predictionContent}>
                  {/* 리포트 헤더: 타이틀 선택 박스 + 복사/프롬프트 버튼 + PSYNET */}
                  <div style={styles.predictionHeaderRow}>
                    {/* 리포트 선택 (선택박스) + 복사/프롬프트 버튼 */}
                    <div style={styles.reportSelectGroup}>
                      <select
                        style={styles.reportSelect}
                        value={selectedPredictionIndex}
                        onChange={(e) => setSelectedPredictionIndex(Number(e.target.value))}
                      >
                        {allPredictions.map((pred, index) => (
                          <option key={pred.prediction_id} value={index}>
                            {pred.ai_model.toUpperCase()} v{pred.version} - {new Date(pred.created_at).toLocaleString('ko-KR')}
                          </option>
                        ))}
                      </select>
                      <button
                        style={styles.reportCopyButton}
                        onClick={() => copyToClipboard(allPredictions[selectedPredictionIndex].report)}
                        title="리포트 복사"
                      >
                        복사
                      </button>
                      <button
                        style={styles.promptViewButton}
                        onClick={() => {
                          const prompt = allPredictions[selectedPredictionIndex].prompt_template;
                          if (prompt) {
                            setModalContent(prompt);
                            setShowPromptModal(true);
                          } else {
                            toast.info('이 리포트에 사용된 프롬프트가 없음.');
                          }
                        }}
                        title="프롬프트 보기"
                      >
                        프롬프트
                      </button>
                    </div>
                    {/* PSYNET 발행 버튼 - administrator만 사용 가능 */}
                    {isAdministrator && (
                      <button
                        onClick={handlePublishToPsynet}
                        disabled={publishingToPsynet || isLoading}
                        style={{
                          ...styles.psynetButton,
                          opacity: (publishingToPsynet || isLoading) ? 0.6 : 1,
                          cursor: (publishingToPsynet || isLoading) ? 'not-allowed' : 'pointer'
                        }}
                        title='DB에서 최신 예측글을 조회하여 Psynet에 자동 발행'
                      >
                        {publishingToPsynet ? '발행 중...' : 'PSYNET 발행'}
                      </button>
                    )}
                  </div>
                  {/* 예측 리포트 메타 정보 */}
                  <div style={styles.predictionMeta}>
                    <span style={styles.predictionMetaItem}>
                      <strong>AI 모델:</strong> {allPredictions[selectedPredictionIndex].ai_model.toUpperCase()}
                    </span>
                    <span style={styles.predictionMetaItem}>
                      <strong>버전:</strong> v{allPredictions[selectedPredictionIndex].version}
                    </span>
                    {allPredictions[selectedPredictionIndex].admin_name && (
                      <span style={styles.predictionMetaItem}>
                        <strong>담당자:</strong> {allPredictions[selectedPredictionIndex].admin_name}
                      </span>
                    )}
                    <span style={styles.predictionMetaItem}>
                      <strong>생성일:</strong> {new Date(allPredictions[selectedPredictionIndex].created_at).toLocaleString('ko-KR')}
                    </span>
                  </div>
                  <div style={styles.prediction}>
                    <pre style={styles.pre}>{allPredictions[selectedPredictionIndex].report}</pre>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={styles.predictionContent}>
              <p style={{textAlign: 'center', color: '#6c757d', padding: '20px'}}>
                아직 생성된 예측 리포트가 없음.
              </p>
            </div>
          )}

          {currentMatch?.notion_page_id && (
            <div style={styles.notionLink}>
              <a
                href={`https://notion.so/${currentMatch.notion_page_id.replace(/-/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.link}
              >
                Notion 페이지 보기
              </a>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default PredictionPanel;
